import { useState } from 'react';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@components/common/Card';
import { Button } from '@components/common/Button';
import { Input } from '@components/common/Input';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { useListUsers, useDeleteUser } from '@api/hooks/useUsers';
import { UserForm } from './UserForm';
import { RoleSelector } from './RoleSelector';
import type { AdminUser } from '@schemas/api';

export const AdminUserManagement = () => {
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showRoleSelector, setShowRoleSelector] = useState<string | null>(null);

  const limit = 10;
  const { data: usersData, isLoading } = useListUsers(skip, limit, search);
  const deleteUserMutation = useDeleteUser();

  const users = usersData?.items || [];
  const total = usersData?.total || 0;
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUserMutation.mutateAsync(userId);
      } catch (error) {
        console.error('Failed to delete user:', error);
      }
    }
  };

  const handleFormSuccess = () => {
    setShowCreateForm(false);
    setSelectedUser(null);
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>User Management</CardTitle>
            <Button
              variant="primary"
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New User
            </Button>
          </div>
        </CardHeader>

        {showCreateForm && (
          <div className="p-6 border-b border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Create New User</h3>
            <UserForm
              onSuccess={handleFormSuccess}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        )}

        <div className="p-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search users by name or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSkip(0);
              }}
              className="pl-10"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left py-3 px-4 font-semibold">Name</th>
                  <th className="text-left py-3 px-4 font-semibold">Email</th>
                  <th className="text-left py-3 px-4 font-semibold">Status</th>
                  <th className="text-left py-3 px-4 font-semibold">Role</th>
                  <th className="text-right py-3 px-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user: AdminUser) => (
                  <tr
                    key={user.id}
                    className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50"
                  >
                    <td className="py-3 px-4">{`${user.first_name} ${user.last_name}`}</td>
                    <td className="py-3 px-4">{user.email}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                          user.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        }`}
                      >
                        {user.status || 'active'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {user.is_admin && (
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          Admin
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowRoleSelector(user.id)}
                      >
                        Roles
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setSelectedUser(user)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={deleteUserMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center pt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing {skip + 1}-{Math.min(skip + limit, total)} of {total} users
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={skip === 0}
                onClick={() => setSkip(Math.max(0, skip - limit))}
              >
                Previous
              </Button>
              <span className="text-sm py-2 px-4">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                variant="secondary"
                disabled={currentPage >= totalPages}
                onClick={() => setSkip(skip + limit)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {selectedUser && (
        <Card>
          <CardHeader>
            <CardTitle>Edit User: {`${selectedUser.first_name} ${selectedUser.last_name}`}</CardTitle>
          </CardHeader>
          <div className="p-6">
            <UserForm
              user={selectedUser}
              onSuccess={handleFormSuccess}
              onCancel={() => setSelectedUser(null)}
            />
          </div>
        </Card>
      )}

      {showRoleSelector && (
        <Card>
          <CardHeader>
            <CardTitle>
              Manage Roles for {(() => { const u = users.find((u: AdminUser) => u.id === showRoleSelector); return u ? `${u.first_name} ${u.last_name}` : ''; })()}
            </CardTitle>
          </CardHeader>
          <div className="p-6">
            <RoleSelector
              userId={showRoleSelector}
              onSuccess={() => setShowRoleSelector(null)}
            />
            <Button
              variant="secondary"
              onClick={() => setShowRoleSelector(null)}
              className="mt-4"
            >
              Done
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};
